// R-131-OVERVIEW-UX-D — shared status pill (consumed by R-131-OVERVIEW-UX-B).
import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type StatusPillVariant =
  | 'using-defaults'
  | 'custom'
  | 'success'
  | 'warn'
  | 'conditional-high'
  | 'danger'
  | 'neutral';

const VARIANT_CLASS: Record<StatusPillVariant, string> = {
  'using-defaults': 'bg-state-hover text-text-muted',
  custom: 'bg-primary/10 text-primary',
  success: 'bg-success/10 text-success',
  warn: 'bg-warning/10 text-warning',
  // Aurora CONDITIONAL-HIGH band — theme-aware --conditional-high token
  // (dark amber-orange / light burnt-orange) rather than a raw orange palette.
  'conditional-high': 'bg-conditional-high/10 text-conditional-high',
  danger: 'bg-danger/10 text-danger',
  neutral: 'bg-state-hover text-text-secondary',
};

export interface StatusPillProps {
  variant?: StatusPillVariant;
  children: React.ReactNode;
  icon?: LucideIcon;
  className?: string;
  title?: string;
  'data-testid'?: string;
  /** Forwarded verbatim (e.g. `data-citable`, `data-citation-reason` from the product citation policy). */
  [dataAttribute: `data-${string}`]: string | undefined;
}

export const StatusPill: React.FC<StatusPillProps> = ({
  variant = 'neutral',
  children,
  icon: Icon,
  className,
  title,
  ...dataAttributes
}) => (
  <span
    {...dataAttributes}
    title={title}
    className={cn(
      'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium',
      VARIANT_CLASS[variant],
      className,
    )}
  >
    {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden />}
    {children}
  </span>
);
